// ─────────────────────────────────────────────────────────────────────────────
// useNodesData — fetches the fleet + KPI summary for the Nodes page.
//   GET /enterprise-nodes          → node list
//   GET /enterprise-nodes/summary  → the four KPI tiles
//
// How this survives a backend that is down, slow, or throwing:
//
//   • The two calls are independent. Either can fail without taking the other
//     down, so a broken /summary still leaves you a working table.
//
//   • Last good data is kept. A refresh that fails does NOT blank the page — it
//     keeps the rows you were looking at and raises `error` alongside them, so
//     the UI can show a "couldn't refresh" strip over live content instead of
//     throwing your data away because one poll failed.
//
//   • `hasLoaded` separates "no nodes" from "we never got an answer". Without
//     it an outage renders as an empty fleet, which is a lie the user acts on.
//
//   • Out-of-order and post-unmount responses are dropped (see requestRef), so
//     a slow first request can't overwrite a fast second one, and navigating
//     away mid-flight doesn't set state on a dead component.
//
// Errors arrive already phrased for display (see nodesService). Nothing here
// puts a status code or server string on screen.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useEffect, useCallback, useRef } from "react";
import { fetchNodes, fetchNodesSummary } from "../../../services/nodesService";

const GENERIC_ERROR = "Something went wrong loading node data.";

export function useNodesData() {
  const [nodes, setNodes]           = useState([]);
  const [summary, setSummary]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);        // fleet unavailable
  const [summaryError, setSummaryError] = useState(null);    // KPIs unavailable
  const [hasLoaded, setHasLoaded]   = useState(false);       // ever got the fleet

  // Monotonic id of the newest request; anything older is ignored when it lands.
  // Reset to -1 on unmount so every in-flight response is discarded.
  const requestRef = useRef(0);
  useEffect(() => () => { requestRef.current = -1; }, []);

  const refetch = useCallback(async () => {
    const id = requestRef.current + 1;
    requestRef.current = id;
    setLoading(true);

    const [nodesRes, summaryRes] = await Promise.allSettled([
      fetchNodes(),
      fetchNodesSummary(),
    ]);

    if (requestRef.current !== id) return;   // superseded, or unmounted

    if (nodesRes.status === "fulfilled") {
      setNodes(nodesRes.value);
      setHasLoaded(true);
      setError(null);
    } else {
      // Keep whatever rows we already have; report the failure next to them.
      setError(nodesRes.reason?.message || GENERIC_ERROR);
    }

    if (summaryRes.status === "fulfilled") {
      setSummary(summaryRes.value);
      setSummaryError(null);
    } else {
      setSummaryError(summaryRes.reason?.message || GENERIC_ERROR);
    }

    setLoading(false);
  }, []);

  useEffect(() => { refetch(); }, [refetch]);

  return { nodes, summary, loading, error, summaryError, hasLoaded, refetch };
}
